import {
  addDays,
  endOfWeek,
  format,
  formatISO,
  isValid,
  parseISO,
  startOfWeek,
} from "date-fns";
import { es } from "date-fns/locale";

import {
  SalesData,
  StatusDistribution,
  TimePeriod,
} from "~/pages/purchase-orders/types/sales-notes";
import { SalesRequest } from "~/types/client";

// Function to get display name for a status
export const getStatusDisplayName = (status: string): string => {
  switch (status) {
    case "accepted":
      return "Aprobadas";
    case "por_facturar":
      return "Por Facturar";
    case "pending":
      return "Autorización Cobranza";
    case "pending_pricing":
      return "Autorización por Precio";
    case "pending_credit":
      return "Crédito Insuficiente";
    case "dispatch_ready":
      return "Con Guía";
    case "prepaid":
      return "Anticipado Pendiente de Pago";
    case "payment_to_check":
      return "Validación Pago";
    case "rejected":
      return "Rechazadas";
    case "in_transit":
      return "En Camino";
    case "delivered":
      return "Entregado";
    case "test":
      return "Test";
    case "unknown":
      return "Desconocido";
    default:
      return (
        status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")
      );
  }
};

// Process data for the sales chart
export const processChartData = (
  requests: SalesRequest[],
  period: TimePeriod = "daily"
): SalesData[] => {
  if (!requests.length) return [];

  // Process sales data based on time period
  const salesByDate: Map<string, { sales: number; orders: number }> = new Map();

  // Sort requests by date
  const sortedRequests = [...requests].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  // Create date buckets based on selected time period
  if (sortedRequests.length > 0) {
    // Find min and max dates for creating the date range
    const minDate = new Date(sortedRequests[0].createdAt);
    const maxDate = new Date(
      sortedRequests[sortedRequests.length - 1].createdAt
    );

    // Ensure maxDate is at least today if max from data is in the past
    const today = new Date();
    const effectiveMaxDate = maxDate < today ? today : maxDate;

    // Generate all dates in the range
    let currentDate = new Date(minDate);
    while (currentDate <= effectiveMaxDate) {
      const dateKey = formatISO(currentDate, { representation: "date" });
      // Initialize with zero values
      salesByDate.set(dateKey, { sales: 0, orders: 0 });
      // Increment by one day
      currentDate = addDays(currentDate, 1);
    }
  }

  // Aggregate request data into the appropriate buckets
  requests.forEach((request) => {
    if (!request.createdAt) return;
    const requestDate = new Date(request.createdAt);
    if (!isValid(requestDate)) return;

    const dateKey = formatISO(requestDate, { representation: "date" });
    // Add to daily data (we need this for all periods)
    const dailyData = salesByDate.get(dateKey) || { sales: 0, orders: 0 };
    dailyData.sales += request.totalAmount;
    dailyData.orders += 1;
    salesByDate.set(dateKey, dailyData);
  });

  // Convert to array and format based on selected period
  let formattedData: SalesData[] = [];

  if (period === "daily") {
    // For daily view, use the data as is
    formattedData = Array.from(salesByDate.entries()).map(([dateStr, data]) => {
      const date = parseISO(dateStr);
      return {
        date: dateStr,
        formattedDate: format(date, "dd MMM", { locale: es }),
        sales: data.sales,
        orders: data.orders,
      };
    });
  } else if (period === "weekly") {
    // For weekly view, group by week
    const weeklyData: Map<
      string,
      { sales: number; orders: number; startDate: Date }
    > = new Map();

    Array.from(salesByDate.entries()).forEach(([dateStr, data]) => {
      const date = parseISO(dateStr);
      const weekStart = startOfWeek(date, { weekStartsOn: 1 }); // Week starts on Monday
      const weekKey = formatISO(weekStart, { representation: "date" });

      if (!weeklyData.has(weekKey)) {
        weeklyData.set(weekKey, {
          sales: 0,
          orders: 0,
          startDate: weekStart,
        });
      }

      const weekData = weeklyData.get(weekKey)!;
      weekData.sales += data.sales;
      weekData.orders += data.orders;
    });

    formattedData = Array.from(weeklyData.entries()).map(([_, data]) => {
      const weekEnd = endOfWeek(data.startDate, { weekStartsOn: 1 });
      return {
        date: formatISO(data.startDate, { representation: "date" }),
        formattedDate: `${format(data.startDate, "dd MMM", {
          locale: es,
        })} - ${format(weekEnd, "dd MMM", { locale: es })}`,
        sales: data.sales,
        orders: data.orders,
      };
    });
  } else if (period === "monthly") {
    // For monthly view, group by month
    const monthlyData: Map<string, { sales: number; orders: number }> =
      new Map();

    Array.from(salesByDate.entries()).forEach(([dateStr, data]) => {
      const date = parseISO(dateStr);
      const monthKey = format(date, "yyyy-MM");

      if (!monthlyData.has(monthKey)) {
        monthlyData.set(monthKey, { sales: 0, orders: 0 });
      }

      const monthData = monthlyData.get(monthKey)!;
      monthData.sales += data.sales;
      monthData.orders += data.orders;
    });

    formattedData = Array.from(monthlyData.entries()).map(
      ([monthKey, data]) => {
        const [year, month] = monthKey.split("-");
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return {
          date: monthKey,
          formattedDate: format(date, "MMM yyyy", { locale: es }),
          sales: data.sales,
          orders: data.orders,
        };
      }
    );
  }

  // Sort by date
  formattedData.sort((a, b) => a.date.localeCompare(b.date));
  return formattedData;
};

// Process status distribution for pie chart
export const processStatusDistribution = (
  requests: SalesRequest[]
): StatusDistribution[] => {
  // Dynamically collect all unique statuses present in the data
  const statusCounts: Record<string, number> = {};

  requests.forEach((request) => {
    const status = request.status || "unknown";
    statusCounts[status] = (statusCounts[status] || 0) + 1;
  });

  // Define a color palette for statuses
  const statusColors: Record<string, string> = {
    accepted: "#00C49F",
    pending: "#FFBB28",
    rejected: "#FF8042",
    in_transit: "#0088FE",
    delivered: "#8884D8",
    unknown: "#A9A9A9",
  };

  // Transform to the format needed for the pie chart
  const statusData = Object.entries(statusCounts).map(([status, count]) => ({
    name: getStatusDisplayName(status),
    value: count,
    color:
      statusColors[status] ||
      "#" + Math.floor(Math.random() * 16777215).toString(16), // Random color for unknown statuses
  }));

  return statusData;
};
